import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import logo from "../assets/trustauto-logo.png";
import carAuction from "../assets/car-auction.svg";
import myCarImg from "../assets/my-car.png";
import tenderImg from "../assets/tender.png";
import profile from "../assets/profile.svg";

import { ImageWithFallback } from "./Utilities";
import "./Header.css";
import { useCarContext } from "../CarContext";
import { useUser } from "../../contexts/UserContext";

// Storage keys for sessionStorage
const STORAGE_KEYS = {
  searchValue: "dealerHome_searchValue",
  locationValue: "dealerHome_locationValue",
  sortValue: "dealerHome_sortValue",
  selectedBrand: "dealerHome_selectedBrand",
  expandedSections: "dealerHome_expandedSections",
  selectedFilters: "dealerHome_selectedFilters",
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sliderRef = useRef(null);
  const mobileSliderRef = useRef(null);
  const {
    carCount,
    uploadPicturesCount,
    placeBidsCount,
    paymentsCount,
    takeDeliveryCount,
    nocCount,
    filteredCarCount,
  } = useCarContext();
  const { currentUser, loading, logout } = useUser();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Calculate total count for My Cars badge
  const myCarsCount =
    carCount +
    uploadPicturesCount +
    placeBidsCount +
    paymentsCount +
    takeDeliveryCount +
    nocCount;

  const handleMyCarsClick = () => {
    navigate("/mycars/inspection");
    setIsDropdownOpen(false);
  };

  const handleTenderClick = () => {
    setIsDropdownOpen(false);
  };

  const handleCarAuctionClick = () => {
    navigate("/DealerHome");
    setIsDropdownOpen(false);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem("onboardingStatus");
      Object.values(STORAGE_KEYS).forEach((key) =>
        sessionStorage.removeItem(key)
      );
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      navigate("/");
    }
    setIsDropdownOpen(false);
  };

  // Enhanced isActive function to handle initial render
  const isActive = (path) => {
    const normalizedPath = location.pathname.toLowerCase().replace(/\/$/, "");
    const normalizedTarget = path.toLowerCase().replace(/\/$/, "");
    // Force /DealerHome to be active on initial load if it's the current path
    return (
      normalizedPath === normalizedTarget ||
      normalizedPath.startsWith(normalizedTarget + "/") ||
      (normalizedPath === "/DealerHome" && normalizedTarget === "/DealerHome")
    );
  };

  // Function to update slider position and size
  const updateSlider = () => {
    const isDesktop = window.innerWidth >= 1024;
    const activeItem = isDesktop
      ? document.querySelector(".nav .nav-item.active")
      : document.querySelector(".mobile-nav .nav-item.active");
    const slider = isDesktop ? sliderRef.current : mobileSliderRef.current;

    if (activeItem && slider) {
      window.getComputedStyle(activeItem).getPropertyValue("width");
      const { offsetLeft, offsetWidth, offsetHeight } = activeItem;
      slider.style.width = `${offsetWidth}px`;
      slider.style.left = `${offsetLeft}px`;
      slider.style.height = `${offsetHeight}px`;
      slider.style.opacity = "1";
    } else if (slider) {
      slider.style.opacity = "0";
    }
  };

  // Ensure slider updates on mount and route change
  useEffect(() => {
    requestAnimationFrame(updateSlider);
  }, []); // Run on mount

  useEffect(() => {
    requestAnimationFrame(updateSlider);
    const handleResize = () => requestAnimationFrame(updateSlider);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [location.pathname]);

  // ResizeObserver for dynamic size changes
  useEffect(() => {
    const items = document.querySelectorAll(".nav-item");
    if (items.length === 0) return;

    const observer = new ResizeObserver(() =>
      requestAnimationFrame(updateSlider)
    );
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <div className="logo">
                <ImageWithFallback src={logo} alt="Trust Auto Logo" />
              </div>
              <nav className="nav">
                <div className="nav-tabs-container">
                  <div className="slider" ref={sliderRef}></div>
                  <div
                    className={`nav-item ${
                      isActive("/DealerHome") ? "active" : ""
                    }`}
                    onClick={handleCarAuctionClick}
                  >
                    <ImageWithFallback
                      src={carAuction}
                      alt="Car Auction Icon"
                    />
                    <span>Browse Cars</span>
                    <span className="badge">{filteredCarCount}</span>
                  </div>
                  <div
                    className={`nav-item ${
                      isActive("/mycars") ? "active" : ""
                    }`}
                    onClick={handleMyCarsClick}
                  >
                    <ImageWithFallback src={myCarImg} alt="My Cars Icon" />
                    <span>My Cars</span>
                    <span className="badge">{myCarsCount}</span>
                  </div>
                </div>
              </nav>
            </div>
            <div className="header-actions">
              <p className="welcome-name">
                {loading
                  ? "Loading..."
                  : `Welcome, ${
                     currentUser?.username||
                      currentUser?.email ||
                      localStorage.getItem("userName") ||
                      localStorage.getItem("email") ||
                      "User"
                    }`}
              </p>
              <div className="user-avatar" onClick={handleProfileClick}>
                {loading ? (
                  <div className="loading-spinner">⏳</div>
                ) : (
                  <div className="profile-pic">
                    {currentUser?.profileImageUrl ||
                    localStorage.getItem("profileImageUrl") ? (
                      <img
                        src={
                          currentUser?.profileImageUrl ||
                          localStorage.getItem("profileImageUrl")
                        }
                        alt={
                          currentUser?.name ||
                          localStorage.getItem("userName") ||
                          "User"
                        }
                        className="preview-image"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "block";
                        }}
                      />
                    ) : null}
                    <ImageWithFallback
                    className="preview-image"
                      src={profile}
                      alt="Profile"
                      style={{
                        display:
                          currentUser?.profileImageUrl ||
                          localStorage.getItem("profileImageUrl")
                            ? "none"
                            : "block",
                      }}
                    />
                  </div>
                )}
                {isDropdownOpen && (
                  <div className="dropdown-header">
                    <button className="dropdown-item" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <nav className="mobile-nav">
          <div className="header-actions mobile-header">
            <div className="nav-tabs-container">
              <div className="slider" ref={mobileSliderRef}></div>
              <div
                className={`nav-item ${
                  isActive("/DealerHome") ? "active" : ""
                }`}
                onClick={handleCarAuctionClick}
              >
                <ImageWithFallback src={carAuction} alt="Car Auction Icon" />
                <p>Browse Cars</p>
                <span className="badge">{filteredCarCount}</span>
              </div>
              <div
                className={`nav-item ${isActive("/mycars") ? "active" : ""}`}
                onClick={handleMyCarsClick}
              >
                <ImageWithFallback src={myCarImg} alt="My Cars Icon" />
                <p>My Cars</p>
                <span className="badge">{myCarsCount}</span>
              </div>
            </div>
            <div className="mobile-user-avatar" onClick={handleProfileClick}>
              {loading ? (
                <div className="loading-spinner">⏳</div>
              ) : (
                <div className="profile-pic">
                  {currentUser?.profileImageUrl ||
                  localStorage.getItem("profileImageUrl") ? (

                    <img
                      src={
                        currentUser?.profileImageUrl ||
                        localStorage.getItem("profileImageUrl")
                      }
                      alt={
                        currentUser?.name ||
                        localStorage.getItem("userName") ||
                        "User"
                      }
                      className="preview-image"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "block";
                      }}
                    />
                  ) : null}
                  <ImageWithFallback
                    src={profile}
                    className="preview-image"
                    alt="Profile"
                    style={{
                      display:
                        currentUser?.profileImageUrl ||
                        localStorage.getItem("profileImageUrl")
                          ? "none"
                          : "block",
                    }}
                  />
                </div>
              )}
              {isDropdownOpen && (
                <div className="dropdown-header">
                  <button className="dropdown-item" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>
      <Outlet />
    </>
  );
};

export default Header;
